import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";

const KmMapsPage = () => {
  const [logs, setLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [filters, setFilters] = useState({
    employee_id: "",
    vehicle_id: "",
    status: "",
    start_date: "",
    end_date: "",
    tenant_id: ""
  });
  
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const canApprove = ["super_admin", "tenant_admin", "hr_manager"].includes(user?.role);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const [logsRes, summaryRes, employeesRes, vehiclesRes] = await Promise.all([
        api.get(`/hr/km-maps?${params.toString()}`),
        api.get(`/hr/km-maps/summary?${params.toString()}`),
        api.get("/hr/km-maps/employees" + (filters.tenant_id ? `?tenant_id=${filters.tenant_id}` : "")),
        api.get("/hr/vehicles" + (filters.tenant_id ? `?tenant_id=${filters.tenant_id}` : ""))
      ]);
      
      setLogs(logsRes.data);
      setSummary(summaryRes.data);
      setEmployees(employeesRes.data);
      setVehicles(vehiclesRes.data);
      
      if (isSuperAdmin) {
        const tenantsRes = await api.get("/tenants");
        setTenants(tenantsRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (logId, action) => {
    try {
      await api.put(`/hr/km-maps/${logId}/approve?action=${action}`);
      toast.success(action === "approve" ? "Registo aprovado" : "Registo rejeitado");
      fetchData();
    } catch (error) {
      toast.error("Erro ao processar registo");
    }
  };

  const handleBulkApprove = async (action) => {
    if (selectedLogs.length === 0) {
      toast.error("Selecione pelo menos um registo");
      return;
    }
    try {
      await api.put("/hr/km-maps/bulk-approve", {
        log_ids: selectedLogs,
        action: action
      });
      toast.success(`${selectedLogs.length} registos ${action === "approve" ? "aprovados" : "rejeitados"}`);
      setSelectedLogs([]);
      fetchData();
    } catch (error) {
      toast.error("Erro ao processar registos");
    }
  };

  const handleExportPDF = async (employeeId = null) => {
    try {
      const params = new URLSearchParams();
      if (employeeId) params.append("employee_id", employeeId);
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== "employee_id") params.append(key, value);
      });
      
      const response = await api.get(`/hr/km-maps/export/pdf?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `mapa_km_${employeeId || "global"}_${new Date().toISOString().split("T")[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF exportado com sucesso");
    } catch (error) {
      toast.error("Erro ao exportar PDF");
    }
  };

  const handleExportExcel = async (employeeId = null) => {
    try {
      const params = new URLSearchParams();
      if (employeeId) params.append("employee_id", employeeId);
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== "employee_id") params.append(key, value);
      });
      
      const response = await api.get(`/hr/km-maps/export/excel?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `mapa_km_${employeeId || "global"}_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Excel exportado com sucesso");
    } catch (error) {
      toast.error("Erro ao exportar Excel");
    }
  };

  const toggleSelectLog = (logId) => {
    setSelectedLogs(prev => 
      prev.includes(logId) ? prev.filter(id => id !== logId) : [...prev, logId]
    );
  };

  const toggleSelectAll = () => {
    const pendingLogs = logs.filter(l => l.status === "pending").map(l => l.log_id);
    if (selectedLogs.length === pendingLogs.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(pendingLogs);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: "Pendente", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
      approved: { label: "Aprovado", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" },
      rejected: { label: "Rejeitado", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" }
    };
    const c = config[status] || { label: status, color: "bg-gray-100 text-gray-700" };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.color}`}>{c.label}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="km-maps-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Mapas de Quilómetros</h1>
          <p className="text-muted-foreground">Gestão e aprovação de registos de KM das viaturas</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => handleExportExcel()}
            data-testid="export-km-excel-btn"
            className="px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center gap-2 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Excel Global
          </button>
          <button
            onClick={() => handleExportPDF()}
            data-testid="export-km-pdf-btn"
            className="px-3 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            PDF Global
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="rounded-xl border bg-card p-4">
            <p className="text-2xl font-bold">{summary.total_logs}</p>
            <p className="text-xs text-muted-foreground">Total Registos</p>
          </div>
          <div className="rounded-xl border bg-card p-4">
            <p className="text-2xl font-bold text-amber-600">{summary.pending}</p>
            <p className="text-xs text-muted-foreground">Pendentes</p>
          </div>
          <div className="rounded-xl border bg-card p-4">
            <p className="text-2xl font-bold text-emerald-600">{summary.approved}</p>
            <p className="text-xs text-muted-foreground">Aprovados</p>
          </div>
          <div className="rounded-xl border bg-card p-4">
            <p className="text-2xl font-bold text-indigo-600">{summary.total_km?.toLocaleString("pt-PT")}</p>
            <p className="text-xs text-muted-foreground">Total KM</p>
          </div>
          <div className="rounded-xl border bg-card p-4">
            <p className="text-2xl font-bold text-blue-600">{summary.reimbursable_km?.toLocaleString("pt-PT")}</p>
            <p className="text-xs text-muted-foreground">KM Reembolsáveis</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="rounded-xl border bg-card p-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {isSuperAdmin && (
            <div>
              <label className="block text-xs font-medium mb-1">Tenant</label>
              <select
                value={filters.tenant_id}
                onChange={(e) => setFilters({ ...filters, tenant_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm"
                data-testid="tenant-filter"
              >
                <option value="">Todos os Tenants</option>
                {tenants.map((t) => (
                  <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium mb-1">Colaborador</label>
            <select
              value={filters.employee_id}
              onChange={(e) => setFilters({ ...filters, employee_id: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm"
              data-testid="employee-filter"
            >
              <option value="">Todos</option>
              {employees.map((e) => (
                <option key={e.employee_id} value={e.employee_id}>{e.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Viatura</label>
            <select
              value={filters.vehicle_id}
              onChange={(e) => setFilters({ ...filters, vehicle_id: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm"
            >
              <option value="">Todas</option>
              {vehicles.map((v) => (
                <option key={v.vehicle_id} value={v.vehicle_id}>{v.license_plate} - {v.brand} {v.model}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Estado</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm"
            >
              <option value="">Todos</option>
              <option value="pending">Pendentes</option>
              <option value="approved">Aprovados</option>
              <option value="rejected">Rejeitados</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Data Início</label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Data Fim</label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm"
            />
          </div>
        </div>
        
        {/* Export by employee */}
        {filters.employee_id && (
          <div className="mt-4 pt-4 border-t flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Exportar para colaborador selecionado:</span>
            <button
              onClick={() => handleExportExcel(filters.employee_id)}
              className="px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 text-sm"
            >
              Excel
            </button>
            <button
              onClick={() => handleExportPDF(filters.employee_id)}
              className="px-3 py-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 text-sm"
            >
              PDF
            </button>
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {canApprove && selectedLogs.length > 0 && (
        <div className="flex items-center gap-4 p-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800">
          <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
            {selectedLogs.length} registo(s) selecionado(s)
          </span>
          <button
            onClick={() => handleBulkApprove("approve")}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm"
          >
            Aprovar Selecionados
          </button>
          <button
            onClick={() => handleBulkApprove("reject")}
            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm"
          >
            Rejeitar Selecionados
          </button>
          <button
            onClick={() => setSelectedLogs([])}
            className="px-4 py-2 rounded-lg border border-input hover:bg-muted text-sm"
          >
            Limpar Seleção
          </button>
        </div>
      )}

      {/* Table */}
      {logs.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border">
          <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-muted-foreground">Nenhum registo de KM encontrado</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  {canApprove && (
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedLogs.length === logs.filter(l => l.status === "pending").length && selectedLogs.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Data</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Colaborador</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Viatura</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Origem → Destino</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">KM</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Diferença</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Reemb.</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Estado</th>
                  {canApprove && <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Ações</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {logs.map((log) => (
                  <tr key={log.log_id} className="hover:bg-muted/30">
                    {canApprove && (
                      <td className="px-4 py-3">
                        {log.status === "pending" && (
                          <input
                            type="checkbox"
                            checked={selectedLogs.includes(log.log_id)}
                            onChange={() => toggleSelectLog(log.log_id)}
                            className="rounded border-gray-300"
                          />
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm">{log.date}</td>
                    <td className="px-4 py-3 text-sm font-medium">{log.employee_name}</td>
                    <td className="px-4 py-3 text-sm">{log.vehicle_info || log.license_plate || "-"}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="text-muted-foreground">{log.origin || "-"}</span>
                      <span className="mx-1">→</span>
                      <span>{log.destination || "-"}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="text-muted-foreground">{log.km_previous || log.km_start || "-"}</span>
                      <span className="mx-1">→</span>
                      <span className="font-medium">{log.km || log.km_end || "-"}</span>
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-indigo-600">
                      {(log.km_difference || log.distance || 0).toLocaleString("pt-PT")} km
                    </td>
                    <td className="px-4 py-3">
                      {log.reimbursable ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">Sim</span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-500">Não</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(log.status)}</td>
                    {canApprove && (
                      <td className="px-4 py-3">
                        {log.status === "pending" && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleApprove(log.log_id, "approve")}
                              className="p-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                              title="Aprovar"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleApprove(log.log_id, "reject")}
                              className="p-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200"
                              title="Rejeitar"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        )}
                        {log.status !== "pending" && log.approved_by && (
                          <span className="text-xs text-muted-foreground">
                            Por: {log.approved_by}
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top employees by KM */}
      {summary?.by_employee?.length > 0 && (
        <div className="rounded-xl border bg-card p-4">
          <h3 className="text-lg font-semibold mb-4">Top Colaboradores por KM</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {summary.by_employee.slice(0, 5).map((emp, idx) => (
              <div key={emp._id} className="p-3 rounded-lg bg-muted/30 flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${idx === 0 ? "bg-amber-500" : idx === 1 ? "bg-slate-400" : idx === 2 ? "bg-amber-700" : "bg-slate-300"}`}>
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{emp.employee_name}</p>
                  <p className="text-xs text-muted-foreground">{emp.total_km?.toLocaleString("pt-PT")} km ({emp.count} registos)</p>
                </div>
                <button
                  onClick={() => handleExportPDF(emp._id)}
                  className="p-1.5 rounded bg-red-100 text-red-600 hover:bg-red-200"
                  title="Exportar PDF"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default KmMapsPage;
